import { Logo } from './icons/Logo';
import React, { useState, useEffect } from 'react';
import { initializeSupabase } from '../lib/supabase';
import { configService } from '../lib/config';
import i18n from '../lib/i18n';

interface AppInitializerProps {
  children: React.ReactNode;
}

export const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const [backgroundInitComplete, setBackgroundInitComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const initializeApp = async () => {
      try {
        console.log('🚀 [AppInitializer] Starting background initialization...');

        // 后台并行初始化配置服务和 i18n
        await Promise.all([
          // 配置服务初始化
          configService.initialize().then(() => {
            console.log('✅ [AppInitializer] Configuration service initialized');
          }).catch((err) => {
            console.warn('⚠️ [AppInitializer] Config init delayed:', err);
          }),

          // i18n 初始化 (缩短超时到 500ms)
          new Promise<void>((resolve) => {
            if (i18n.isInitialized) {
              console.log('✅ [AppInitializer] i18n already initialized');
              resolve();
              return;
            }

            const handleInitialized = () => {
              console.log('✅ [AppInitializer] i18n initialized via event');
              i18n.off('initialized', handleInitialized);
              resolve();
            };

            i18n.on('initialized', handleInitialized);

            // 缩短超时时间,不阻塞渲染
            setTimeout(() => {
              i18n.off('initialized', handleInitialized);
              console.warn('⚠️ [AppInitializer] i18n initialization timeout, proceeding anyway');
              resolve();
            }, 500); // 从 2000ms 缩短到 500ms
          })
        ]);

        if (isCancelled) return;

        // Supabase 后台初始化
        console.log('🔧 [AppInitializer] Initializing Supabase in background...');
        initializeSupabase().then(() => {
          console.log('✅ [AppInitializer] Supabase client initialized');
        }).catch((err) => {
          console.warn('⚠️ [AppInitializer] Supabase initialization delayed:', err);
        });

        console.log('🎉 [AppInitializer] Background initialization completed');

        if (!isCancelled) {
          setBackgroundInitComplete(true);
        }

      } catch (error) {
        if (!isCancelled) {
          console.error('❌ [AppInitializer] Background initialization failed:', error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';
          setError(errorMessage);
        }
      }
    };

    initializeApp();

    return () => {
      isCancelled = true;
    };
  }, []); // 只运行一次

  // 立即渲染应用内容,不等待初始化
  return (
    <>
      {children}

      {/* 后台初始化未完成时显示提示 */}
      {!backgroundInitComplete && !error && (
        <div className="fixed bottom-4 right-4 bg-purple-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-2 z-50">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>正在加载配置...</span>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm max-w-md z-50">
          <p className="font-semibold mb-1">配置加载失败</p>
          <p className="text-xs opacity-90">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 text-xs underline hover:no-underline"
          >
            重新加载
          </button>
        </div>
      )}
    </>
  );
};